import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { ApiariesCard } from '../components/ApiariesCard';
import {
  ErrorNotice,
  Field,
  Notice,
  SelectField,
  Spinner,
  StatusPill,
  formatDate,
  formatMoney,
  fullName,
} from '../components/ui';
import {
  HUMAN_SET_STATUSES,
  PERMISSION_LABELS,
  type Member,
  type MemberEdit,
  type MemberStatus,
  type MemberSubscription,
  type PermissionDefinition,
} from '../types';

export function MemberPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const [member, setMember] = useState<Member | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .get<{ member: Member }>(`/members/${id}`)
      .then((result) => {
        if (!cancelled) setMember(result.member);
      })
      .catch((failure) => {
        if (!cancelled) setError(failure);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error !== null && member === null) {
    return (
      <>
        <p className="muted small">
          <Link to="/members">← Back to members</Link>
        </p>
        <ErrorNotice error={error} />
      </>
    );
  }

  if (member === null) return <Spinner label="Loading member" />;

  return (
    <>
      <p className="muted small">
        <Link to="/members">← Back to members</Link>
      </p>

      <div className="page-head">
        <h1>{fullName(member)}</h1>
        <div className="head-actions">
          <StatusPill status={member.status} />
          {can('members_edit') && !editing ? (
            <button type="button" className="primary" onClick={() => setEditing(true)}>
              Edit
            </button>
          ) : null}
        </div>
      </div>

      {saved ? <Notice kind="success">Changes saved.</Notice> : null}

      {editing ? (
        <EditForm
          member={member}
          onCancel={() => setEditing(false)}
          onSaved={(updated) => {
            setMember(updated);
            setEditing(false);
            setSaved(true);
            window.setTimeout(() => setSaved(false), 4000);
          }}
        />
      ) : (
        <ViewCard member={member} />
      )}

      {can('members_edit') && !editing ? (
        <StatusCard
          member={member}
          onChanged={(updated) => {
            setMember(updated);
            setSaved(true);
            window.setTimeout(() => setSaved(false), 4000);
          }}
        />
      ) : null}

      {can('permissions_manage') && !editing ? (
        <PermissionsCard member={member} onChanged={setMember} />
      ) : null}

      {/* members_view to see them at all; members_edit to change them. */}
      {!editing ? (
        <ApiariesCard memberId={member.id} mode="committee" canEdit={can('members_edit')} />
      ) : null}

      {can('subscriptions_view') && !editing ? <SubscriptionHistoryCard memberId={member.id} /> : null}

      {can('members_edit') && !editing ? <CommitteeInviteCard member={member} /> : null}
    </>
  );
}

function ViewCard({ member }: { member: Member }) {
  const { identity } = useAuth();
  const giftAidEnabled = identity?.settings.gift_aid ?? false;

  const address = [member.address_1, member.address_2, member.town, member.postcode]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="card">
      <dl className="details">
        <Detail label="Address">{address || '—'}</Detail>
        <Detail label="Email">
          {member.email ? <a href={`mailto:${member.email}`}>{member.email}</a> : '—'}
        </Detail>
        <Detail label="Telephone">{member.telephone ?? '—'}</Detail>
        <Detail label="Mobile">{member.mobile ?? '—'}</Detail>
        <Detail label="Date of birth">{formatDate(member.date_of_birth)}</Detail>
        <Detail label="Joined">{formatDate(member.joined_date)}</Detail>
        <Detail label="BBKA number">{member.bbka_number ?? '—'}</Detail>
        <Detail label="Mentor">
          {member.is_mentor ? `Yes, up to ${member.mentor_capacity} mentees` : 'No'}
        </Detail>
        {/* Hidden entirely when the association does not claim Gift Aid —
            a field nobody fills in is clutter on a screen used daily. */}
        <Detail label="Gift Aid" hidden={!giftAidEnabled}>
          {member.gift_aid === null ? (
            <span className="muted">Not asked yet</span>
          ) : member.gift_aid ? (
            `Declared${member.gift_aid_declared_on ? ` on ${formatDate(member.gift_aid_declared_on)}` : ''}`
          ) : (
            'No'
          )}
        </Detail>

        {member.partner_of_name ? (
          <Detail label="Partner member of">
            <Link to={`/members/${member.partner_of_member_id}`}>{member.partner_of_name}</Link>
          </Detail>
        ) : null}

        {member.guardian_name || member.guardian_email ? (
          <Detail label="Guardian">
            {member.guardian_name ?? '—'}
            {member.guardian_email ? (
              <span className="muted block">{member.guardian_email}</span>
            ) : null}
          </Detail>
        ) : null}

        <Detail label="Online account">
          {member.has_login ? (
            <>
              Yes
              <span className="muted block">
                Last signed in {formatDate(member.last_login_at)}
              </span>
            </>
          ) : (
            <>
              No
              <span className="muted block">
                This member has no way to sign in, and will not receive anything that
                depends on one.
              </span>
            </>
          )}
        </Detail>

        {member.permissions.length > 0 ? (
          <Detail label="Committee permissions">
            <ul className="plain">
              {member.permissions.map((code) => (
                <li key={code}>{PERMISSION_LABELS[code] ?? code}</li>
              ))}
            </ul>
          </Detail>
        ) : null}
      </dl>
    </div>
  );
}

function Detail({ label, children, hidden }: { label: string; children: ReactNode; hidden?: boolean }) {
  if (hidden) return null;

  return (
    <div className="detail">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Exactly the fields the API will accept. Anything else is rejected server side. */
function toEdit(member: Member): MemberEdit {
  return {
    title: member.title,
    first_name: member.first_name,
    last_name: member.last_name,
    address_1: member.address_1,
    address_2: member.address_2,
    town: member.town,
    postcode: member.postcode,
    telephone: member.telephone,
    mobile: member.mobile,
    email: member.email,
    date_of_birth: member.date_of_birth,
    guardian_name: member.guardian_name,
    guardian_email: member.guardian_email,
    bbka_number: member.bbka_number,
    joined_date: member.joined_date,
    is_mentor: member.is_mentor,
    mentor_capacity: member.mentor_capacity,
    gift_aid: member.gift_aid,
  };
}

function EditForm({
  member,
  onCancel,
  onSaved,
}: {
  member: Member;
  onCancel: () => void;
  onSaved: (member: Member) => void;
}) {
  const { identity } = useAuth();
  const giftAidEnabled = identity?.settings.gift_aid ?? false;

  const [form, setForm] = useState<MemberEdit>(() => toEdit(member));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  function set<K extends keyof MemberEdit>(key: K, value: MemberEdit[K]) {
    setForm((previous) => ({ ...previous, [key]: value }));
  }

  function text(key: keyof MemberEdit): string {
    const value = form[key];
    return typeof value === 'string' ? value : value === null ? '' : String(value);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.put<{ member: Member }>(`/members/${member.id}`, form);
      onSaved(result.member);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card form wide" onSubmit={onSubmit}>
      <ErrorNotice error={error} />

      <div className="grid">
        <Field label="Title" value={text('title')} onChange={(e) => set('title', e.target.value || null)} />
        <Field
          label="First name"
          required
          value={text('first_name')}
          onChange={(e) => set('first_name', e.target.value)}
        />
        <Field
          label="Last name"
          required
          value={text('last_name')}
          onChange={(e) => set('last_name', e.target.value)}
        />
        <Field
          label="Address line 1"
          value={text('address_1')}
          onChange={(e) => set('address_1', e.target.value || null)}
        />
        <Field
          label="Address line 2"
          value={text('address_2')}
          onChange={(e) => set('address_2', e.target.value || null)}
        />
        <Field label="Town" value={text('town')} onChange={(e) => set('town', e.target.value || null)} />
        <Field
          label="Postcode"
          value={text('postcode')}
          onChange={(e) => set('postcode', e.target.value || null)}
        />
        <Field
          label="Email"
          type="email"
          hint={
            member.has_login
              ? 'This member signs in with this address, so it cannot be one another member already uses to sign in.'
              : 'May be shared with a partner member. Leave blank if they have no email.'
          }
          value={text('email')}
          onChange={(e) => set('email', e.target.value || null)}
        />
        <Field
          label="Telephone"
          value={text('telephone')}
          onChange={(e) => set('telephone', e.target.value || null)}
        />
        <Field
          label="Mobile"
          value={text('mobile')}
          onChange={(e) => set('mobile', e.target.value || null)}
        />
        <Field
          label="Date of birth"
          type="date"
          value={text('date_of_birth')}
          onChange={(e) => set('date_of_birth', e.target.value || null)}
        />
        <Field
          label="Joined"
          type="date"
          value={text('joined_date')}
          onChange={(e) => set('joined_date', e.target.value || null)}
        />
        <Field
          label="BBKA number"
          value={text('bbka_number')}
          onChange={(e) => set('bbka_number', e.target.value || null)}
        />
        <Field
          label="Guardian name"
          hint="For members under 18."
          value={text('guardian_name')}
          onChange={(e) => set('guardian_name', e.target.value || null)}
        />
        <Field
          label="Guardian email"
          type="email"
          value={text('guardian_email')}
          onChange={(e) => set('guardian_email', e.target.value || null)}
        />
        <Field
          label="Mentor capacity"
          type="number"
          min={0}
          max={20}
          value={String(form.mentor_capacity)}
          onChange={(e) => set('mentor_capacity', Number(e.target.value))}
        />
      </div>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={form.is_mentor}
          onChange={(e) => set('is_mentor', e.target.checked)}
        />
        Willing to mentor
      </label>

      {/*
        Three options, not a checkbox. A checkbox can only say yes or no, and
        "nobody has asked them" is a third answer we have to be able to hold —
        exporting "No" for someone who has actually declared costs the
        association 25% of their subscription.

        Hidden when the association does not claim Gift Aid at all.
      */}
      {giftAidEnabled ? (
        <SelectField
          label="Gift Aid declaration"
          value={form.gift_aid === null ? '' : form.gift_aid ? 'yes' : 'no'}
          onChange={(e) =>
            set('gift_aid', e.target.value === '' ? null : e.target.value === 'yes')
          }
        >
          <option value="">Not asked yet</option>
          <option value="yes">Yes, they have declared</option>
          <option value="no">No</option>
        </SelectField>
      ) : null}

      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * Status is separate from the edit form on purpose. Only three of the five
 * values are a human decision — active and lapsed follow from whether this
 * year's subscription is paid, and the API refuses to set them by hand.
 * Putting them in the same form as "town" would invite exactly that mistake.
 */
function StatusCard({
  member,
  onChanged,
}: {
  member: Member;
  onChanged: (member: Member) => void;
}) {
  const [status, setStatus] = useState<MemberStatus>(member.status);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const derived = member.status === 'active' || member.status === 'lapsed';

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.put<{ member: Member }>(`/members/${member.id}/status`, {
        status,
      });
      onChanged(result.member);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card form" onSubmit={onSubmit}>
      <h2>Membership status</h2>
      <p className="muted small">
        {derived
          ? 'This member is currently marked from their subscription record. Setting one of the values below overrides that until their next subscription is recorded.'
          : 'Active and lapsed are worked out from subscriptions and cannot be set here.'}
      </p>

      <ErrorNotice error={error} />

      <SelectField
        label="Set status to"
        value={HUMAN_SET_STATUSES.includes(status) ? status : ''}
        onChange={(event) => setStatus(event.target.value as MemberStatus)}
      >
        <option value="" disabled>
          Choose…
        </option>
        {HUMAN_SET_STATUSES.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </SelectField>

      <div className="actions">
        <button
          type="submit"
          disabled={busy || status === member.status || !HUMAN_SET_STATUSES.includes(status)}
        >
          {busy ? 'Saving…' : 'Update status'}
        </button>
      </div>
    </form>
  );
}

/**
 * Permissions are per person, not per job title — the association's own
 * rule, from the data model. Each checkbox saves itself immediately, the way
 * a settings toggle does, rather than sitting behind a form-wide Save button:
 * a grant or revoke is one small fact, and there is no draft state worth
 * protecting here the way there is on the member's own details.
 *
 * The two-holder floor on "Manage permissions" is enforced server side, not
 * guessed at here — this panel does not know the current holder count, so it
 * always lets the box be unchecked and shows whatever the server says if that
 * would have been the last one.
 */
function PermissionsCard({
  member,
  onChanged,
}: {
  member: Member;
  onChanged: (member: Member) => void;
}) {
  const [catalog, setCatalog] = useState<PermissionDefinition[] | null>(null);
  const [catalogError, setCatalogError] = useState<unknown>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ permissions: PermissionDefinition[] }>('/permissions')
      .then((result) => {
        if (!cancelled) setCatalog(result.permissions);
      })
      .catch((failure) => {
        if (!cancelled) setCatalogError(failure);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function toggle(code: string, currentlyHeld: boolean) {
    setActionError(null);
    setPending(code);
    try {
      const result = currentlyHeld
        ? await api.del<{ member: Member }>(`/members/${member.id}/permissions/${code}`)
        : await api.post<{ member: Member }>(`/members/${member.id}/permissions`, { code });
      onChanged(result.member);
    } catch (failure) {
      setActionError(failure);
    } finally {
      setPending(null);
    }
  }

  // Everything else depends on this one. Without it a permission can be
  // granted and simply does nothing, which is a quiet way to leave somebody
  // locked out while the screen says they have access.
  const inCommittee = member.permissions.includes('committee_area');

  return (
    <div className="card">
      <h2>Committee permissions</h2>

      {!inCommittee ? (
        <Notice kind="info">
          {member.first_name} is not in the committee area, so the rest are switched off — granting
          one would do nothing on its own. Tick <strong>Access the committee area</strong> first.
        </Notice>
      ) : null}

      <p className="muted small">
        At least two members must hold "Manage permissions" at all times, so nobody
        is ever the only person who can grant it back.
      </p>

      <ErrorNotice error={catalogError} />
      <ErrorNotice error={actionError} />

      {catalog === null && catalogError === null ? <Spinner label="Loading permissions" /> : null}

      {catalog !== null ? (
        <ul className="permission-list">
          {catalog.map((permission) => {
            const held = member.permissions.includes(permission.code);
            const isMaster = permission.code === 'committee_area';

            // Only the unticked ones are disabled. Taking a permission away
            // has to stay possible whatever else is true, or somebody who
            // lost the committee area keeps a set of dormant grants that
            // nobody can tidy up.
            const locked = !isMaster && !inCommittee && !held;
            const dormant = !isMaster && !inCommittee && held;

            return (
              <li key={permission.code} className={locked ? 'permission-locked' : undefined}>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={held}
                    disabled={locked || pending === permission.code}
                    onChange={() => toggle(permission.code, held)}
                  />
                  <span>
                    {permission.name}
                    {dormant ? (
                      <span className="pill pill-dormant">does nothing without committee access</span>
                    ) : null}
                    {permission.description ? (
                      <span className="muted block">{permission.description}</span>
                    ) : null}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Why this member is active or lapsed, rather than just asserting it. The
 * status pill at the top is derived from exactly these rows, so showing them
 * is the difference between "the computer says lapsed" and an answer the
 * committee can give someone on the phone.
 */
function SubscriptionHistoryCard({ memberId }: { memberId: number }) {
  const [subscriptions, setSubscriptions] = useState<MemberSubscription[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ subscriptions: MemberSubscription[] }>(`/members/${memberId}/subscriptions`)
      .then((result) => {
        if (!cancelled) setSubscriptions(result.subscriptions);
      })
      .catch((failure) => {
        if (!cancelled) setError(failure);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  return (
    <div className="card">
      <h2>Membership history</h2>
      <ErrorNotice error={error} />

      {subscriptions === null && error === null ? <Spinner label="Loading" /> : null}

      {subscriptions !== null && subscriptions.length === 0 ? (
        <p className="muted small">No subscriptions yet — they have never completed an application.</p>
      ) : null}

      {subscriptions !== null && subscriptions.length > 0 ? (
        <table className="members">
          <thead>
            <tr>
              <th scope="col">Year</th>
              <th scope="col">Category</th>
              <th scope="col">Due</th>
              <th scope="col">Paid</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.map((subscription) => (
              <tr key={subscription.id}>
                <td data-label="Year">{subscription.year}</td>
                <td data-label="Category">
                  {subscription.category_name}
                  {subscription.needs_bdi_referral ? (
                    <span className="badges">
                      <span className="badge">BDI referral</span>
                    </span>
                  ) : null}
                </td>
                <td data-label="Due" className="numeric">
                  {formatMoney(subscription.total_due)}
                </td>
                <td data-label="Paid" className="numeric">
                  {formatMoney(subscription.paid_total)}
                </td>
                <td data-label="Status">
                  <span className={`pill pill-sub-${subscription.status}`}>
                    {subscription.status.replace('_', ' ')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

/**
 * A member can always generate their own partner invite from their own
 * account (see MyAccountPage) — this is the committee-assisted version, for
 * a phone call from someone who is not going to find that themselves.
 */
function CommitteeInviteCard({ member }: { member: Member }) {
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function generate() {
    setError(null);
    setBusy(true);
    try {
      const invite = await api.post<{ token: string }>(`/members/${member.id}/partner-invites`);
      setLink(`${window.location.origin}/join?invite=${invite.token}`);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>Invite a partner member</h2>
      <p className="muted small">
        Generates a link {member.first_name} can pass on to a partner joining under their
        household. Works once, expires in 7 days.
      </p>

      <ErrorNotice error={error} />

      {link === null ? (
        <button type="button" disabled={busy} onClick={() => void generate()}>
          {busy ? 'Generating…' : 'Generate an invite link'}
        </button>
      ) : (
        <div className="invite-link">
          <input type="text" readOnly value={link} onFocus={(e) => e.target.select()} />
        </div>
      )}
    </div>
  );
}
